import * as _ from 'lodash-es';
import type { ContextType } from 'react';
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type {
  DetectOverflowOptions,
  Middleware,
  MiddlewareData,
  Placement,
  Strategy,
} from '@floating-ui/react-dom';
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import Slot from './Slot';
import * as styles from './Tether.module.scss';

const TetherBoundaryPadding = parseInt(styles.boundaryPadding, 10);

const TetherContext = createContext<{
  setArrow: (element: Element | null) => void;
  arrowData: MiddlewareData['arrow'];
  placement: Placement;
} | null>(null);

export const useTetherContext = () => {
  const context = useContext(TetherContext);
  if (!context) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error('TetherContext not found');
    }
    return {
      setArrow: () => {},
      arrowData: undefined,
      placement: 'top',
    } satisfies ContextType<typeof TetherContext>;
  }
  return context;
};

export const TetherIsInStorybookContext = createContext(false);

export interface TetherRef {
  anchorElement: Element | null;
  positionedElement: HTMLElement | null;
  /**
   * Updates the positioned view's position synchronously.
   *
   * Call this when the positioned view is hidden/revealed with CSS and its dimensions
   * remain unchanged, preventing ResizeObserver from detecting the change.
   */
  updatePositionedView: () => void;
}

type FlipBehavior = 'disabled' | 'sameAxisOnly' | 'allowCrossAxis';
type ShiftBehavior = 'disabled' | 'mainAxisOnly';

/**
 * The offset to apply to the positioned view.
 * A number value is equivalent to { mainAxis: number, crossAxis: 0 }.
 * @see https://floating-ui.com/docs/offset
 */
type OffsetValue = number | { mainAxis?: number; crossAxis?: number };
export const parseOffsetValue = (offsetValue: OffsetValue | undefined) =>
  typeof offsetValue === 'number' || offsetValue == null
    ? { mainAxis: offsetValue }
    : {
        mainAxis: offsetValue.mainAxis,
        crossAxis: offsetValue.crossAxis,
      };

export type TetherPortalContainer = HTMLElement | 'disabled';

interface TetherProps {
  positionedView: React.ReactNode;
  /**
   * The element to position the view relative to
   */
  anchorElement: Element | null;
  /**
   * The container element to render the positioned view in via a portal.
   * @default document.body
   */
  portalContainer?: TetherPortalContainer;
  placement?: Placement;
  flipBehavior?: FlipBehavior;
  shiftBehavior?: ShiftBehavior;
  offset?: OffsetValue;
  /**
   * The CSS positioning strategy to use for the positioned view.
   * - 'absolute': relative to the nearest positioned ancestor
   * - 'fixed': relative to the nearest containing block. When `portalContainer`
   *   is disabled, this is useful to prevent the positioned view from being
   *   clipped by ancestor elements with `overflow: hidden` or similar.
   * @default 'absolute'
   */
  strategy?: Strategy;
}

/**
 * A headless component that positions a view relative to the anchor element using floating-ui.
 * It is functionally similar to the CSS anchor API but supports all browsers. It also
 * provides additional features, such as shifting, that would be difficult or impossible
 * to achieve with the CSS anchor API alone.
 *
 * The positioned view must be able to accept props and pass them
 * down to their underlying HTML elements, since it will receive refs, event handlers, etc.
 *
 * It defines a couple of CSS custom properties that can be used by the positioned view's
 * styling:
 * - properties.$tetherAvailableWidth: The width of the available space for the positioned view.
 *   Useful to be set as the max-width of the positioned view to allow shrinking before shifting
 *   or flipping. It's also useful at the same time to set a reasonable min-width to prevent
 *   the positioned view from being too narrow.
 * - properties.$tetherAvailableHeight
 * - properties.$tetherOffsetMainAxis
 * - properties.$tetherOffsetCrossAxis
 *
 * @example
 * <Tether
 *   anchorElement={anchorElement}
 *   positionedView={<div>This will be positioned relative to the children</div>}
 *   placement="bottom"
 * />
 */
const Tether = forwardRef<TetherRef, TetherProps>(
  (
    {
      anchorElement,
      positionedView,
      portalContainer = document.body,
      placement = 'bottom',
      flipBehavior = 'sameAxisOnly',
      shiftBehavior = 'mainAxisOnly',
      offset: unstableOffsetValue = 0,
      strategy = 'absolute',
    },
    ref,
  ) => {
    const isInStorybook = useContext(TetherIsInStorybookContext);

    const [arrowOptions, setArrowOptions] = useState<{
      element: Element;
      size: number;
    } | null>(null);
    const [offsetValue, setOffsetValue] =
      useState<OffsetValue>(unstableOffsetValue);
    if (!_.isEqual(offsetValue, unstableOffsetValue)) {
      setOffsetValue(unstableOffsetValue);
    }

    const middleware = useMemo<(Middleware | undefined)[]>(() => {
      const detectOverflowOptions: DetectOverflowOptions = {
        padding: TetherBoundaryPadding,
        ...(isInStorybook
          ? {
              rootBoundary: 'document', // Prevent viewport height from affecting layout
            }
          : null),
      };

      const { mainAxis: mainAxisOffset = 0, crossAxis: crossAxisOffset = 0 } =
        parseOffsetValue(offsetValue);
      const finalOffsetValue = {
        mainAxis: mainAxisOffset + (arrowOptions?.size ?? 0),
        crossAxis: crossAxisOffset,
      };
      return [
        finalOffsetValue.crossAxis !== 0 || finalOffsetValue.mainAxis !== 0
          ? offset(finalOffsetValue)
          : undefined,
        shiftBehavior === 'disabled'
          ? undefined
          : shift({ ...detectOverflowOptions }),
        size({
          ...detectOverflowOptions,
          apply(args) {
            // Sets up CSS custom properties for styling the positioned view.
            // These properties are set before the `flip` middleware to enable
            // the positioned view to attempt shrinking before flipping.
            // Inspired by https://floating-ui.com/docs/size#initialplacement
            const setProperty = (property: string, pixels: number) => {
              args.elements.floating.style.setProperty(property, `${pixels}px`);
            };
            setProperty(
              styles.propertyAvailableWidth,
              Math.max(args.availableWidth, 0),
            );
            setProperty(
              styles.propertyAvailableHeight,
              Math.max(args.availableHeight, 0),
            );
            setProperty(
              styles.propertyOffsetMainAxis,
              finalOffsetValue.mainAxis,
            );
            setProperty(
              styles.propertyOffsetCrossAxis,
              finalOffsetValue.crossAxis,
            );
          },
        }),
        flipBehavior === 'disabled'
          ? undefined
          : flip({
              ...detectOverflowOptions,
              fallbackAxisSideDirection:
                flipBehavior === 'allowCrossAxis' ? 'end' : 'none',
            }),
        arrow({ element: arrowOptions?.element ?? null, padding: 8 }),
      ];
    }, [flipBehavior, shiftBehavior, offsetValue, arrowOptions, isInStorybook]);

    const {
      placement: actualPlacement,
      refs,
      floatingStyles,
      update,
      middlewareData: { arrow: arrowData },
    } = useFloating<HTMLElement>({
      placement,
      middleware,
      strategy,
      elements: { reference: anchorElement },
      whileElementsMounted: autoUpdate,
    });

    useImperativeHandle(ref, () => ({
      updatePositionedView: update,
      anchorElement: anchorElement ?? null,
      positionedElement: refs.floating.current,
    }));

    const setArrow = useCallback((element: Element | null) => {
      setArrowOptions(element ? { element, size: element.clientHeight } : null);
    }, []);

    const arrowContextValue = useMemo(
      () => ({
        setArrow,
        arrowData,
        placement: actualPlacement,
      }),
      [setArrow, arrowData, actualPlacement],
    );

    const wrappedPositionedView = (
      <Slot ref={refs.setFloating} style={floatingStyles}>
        {positionedView}
      </Slot>
    );
    return (
      <TetherContext.Provider value={arrowContextValue}>
        {!!positionedView &&
          (portalContainer === 'disabled'
            ? wrappedPositionedView
            : createPortal(wrappedPositionedView, portalContainer))}
      </TetherContext.Provider>
    );
  },
);

export default Tether;
